import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import { encodeUrlParam } from "../../utils/urlUtils";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useSelector } from "react-redux";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "./dropdownStyles.js";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import PasswordRequirementsInfo from "../../components/CustomComponents/PasswordRequirementsInfo/PasswordRequirementsInfo.jsx";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

/** Username is set from email in the staff UI — show email wording for duplicate-identity API errors. */
function formatStaffIdentityError(message) {
  const text = String(message ?? "").trim();
  if (!text) return text;
  // API often returns username wording; staff username is the email in the UI
  if (
    /(user\s*name|username)/i.test(text) &&
    (/already\s*exist/i.test(text) || /already\s+a\s+user/i.test(text) || /with\s+given\s+username/i.test(text))
  ) {
    return "Email already exists";
  }
  return text;
}

function AddStaff() {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const appSettings = useSelector((state) => state.appSettings);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [roles, setRoles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [countryCallCodeMap, setCountryCallCodeMap] = useState({});
  const [companyCallCode, setCompanyCallCode] = useState("");

  const formatDialCode = useCallback((raw) => {
    if (!raw || typeof raw !== "string") return "";
    const trimmed = raw.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
  }, []);

  const companyCountry =
    profileData?.compCountry || profileData?.CompCountry || "";
  const companyDialCode = formatDialCode(
    companyCallCode ||
      profileData?.callCode ||
      profileData?.CallCode ||
      countryCallCodeMap[companyCountry] ||
      countryCallCodeMap[(companyCountry || "").toUpperCase()] ||
      ""
  );

  const [staffData, setStaffData] = useState({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    city: "",
    phoneNo: "",
    passCode: "",
    ratePerHour: null,
    active: false,
    userRoles: null,
    managerUserName: "",
    password: "",
    isAllowOverTime: false,
    isFullTimeStaff: false,
    isNewHireStaff: false,
    overTimeHourlyRate: null,
    isSalaryStaff: false,
    salaryType: "Weekly",
    salary: null,
    weeklyHours: null,
    monthlyHours: null,
  });

  const [errors, setErrors] = useState({});

  const [imageState, setImageState] = useState({
    staffImage: "",
    imageFile: null,
  });

  const FetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
        },
      });
      const roleData = response.data.data || response.data;

      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to fetch roles");
    }
  }, [CompID]);

  const FetchManagers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID,
          PageNumber: 1,
          PageSize: 1000, // Fetch all for dropdown
          Search: "",
          SortBy: "UserName",
          SortOrder: "ASC"
        },
      });

      const staffData = response.data.data || response.data;

      if (Array.isArray(staffData)) {
        // Filter out any null or undefined items from the array
        const validStaffs = staffData.filter(staff => staff != null && staff.active);
        const managersOptions = validStaffs.map((staff) => ({
          value: staff.userName,
          label: `${staff.firstName} ${staff.lastName || ''} (${staff.userName})`,
        }));
        setManagers(managersOptions);
      } else {
        // Unexpected API response format silently
        setManagers([]);
      }
    } catch (err) {
      showErrorToast("Failed to fetch managers");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    FetchRoles();
    FetchManagers();
  }, [FetchRoles, FetchManagers]);

  useEffect(() => {
    let cancelled = false;
    const bootstrapDialCode = async () => {
      try {
        const [countriesRes, profileRes] = await Promise.all([
          axiosInstance.get(APIs.GET_COUNTRIES),
          axiosInstance.get(APIs.GET_COMPANY_PROFILE),
        ]);
        if (cancelled) return;

        const list = countriesRes.data?.data || countriesRes.data || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((country) => {
          const callCode = country.callCode ?? country.CallCode ?? "";
          const code = country.code ?? country.Code ?? "";
          const code2 = country.code2 ?? country.Code2 ?? "";
          const name = country.name ?? country.Name ?? "";
          if (code) map[code] = callCode;
          if (code2) map[code2] = callCode;
          if (name) map[name] = callCode;
        });
        setCountryCallCodeMap(map);

        const profile = profileRes.data?.data || {};
        const profileCallCode = profile.callCode || profile.CallCode || "";
        if (profileCallCode) setCompanyCallCode(profileCallCode);
      } catch {
        // Dial code is optional UI adornment; fail silently
      }
    };
    bootstrapDialCode();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const defaultCountry = profileData?.compCountry || profileData?.CompCountry || "UK";
    const defaultCity = profileData?.compCity || profileData?.CompCity || "";
    setStaffData((prev) => ({ ...prev, country: defaultCountry, city: defaultCity }));
  }, [profileData]);

  const validateUsername = (value) => {
    if (!value.trim()) return "Username is required";
    if (value.length < 3) return "Username must be at least 3 characters long";
    return "";
  };

  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.length < 2)
      return "First name must be at least 2 characters long";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (value) => {
    if (!value.trim()) return "Password is required";
    if (value.length < 6) return "Password must be at least 6 characters long";
    if (!/[!@#$%^&*]/.test(value))
      return "Password must contain at least one special character";
    if (!/\d/.test(value)) return "Password must contain at least one digit";
    if (!/[A-Z]/.test(value))
      return "Password must contain at least one uppercase letter";
    return "";
  };

  const generatePassword = (length = 12) => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const digits = "23456789";
    const special = "!@#$%^&*";
    const all = upper + lower + digits + special;
    const pick = (chars) => {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      return chars[arr[0] % chars.length];
    };
    const safeLength = Math.max(8, Math.min(32, Number(length) || 12));
    const chars = [
      pick(upper),
      pick(lower),
      pick(digits),
      pick(special),
    ];
    for (let i = chars.length; i < safeLength; i++) {
      chars.push(pick(all));
    }
    for (let i = chars.length - 1; i > 0; i--) {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      const j = arr[0] % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
  };

  const handleGeneratePassword = (length) => {
    const password = generatePassword(length);
    handleInputChange("password", password, validatePassword);
    setShowPassword(true);
  };

  const handleCopyPassword = async () => {
    if (!staffData.password) {
      showErrorToast("No password to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(staffData.password);
      showSuccessToast("Password copied");
    } catch {
      showErrorToast("Failed to copy password");
    }
  };

  const validateCountry = (value) => {
    if (!value) return "Country is required";
    return "";
  };

  const validateCity = (value) => {
    if (!value) return "City is required";
    return "";
  };

  const validatePassCode = (value) => {
    if (!value.trim()) return "Pass code is required";
    return "";
  };

  const validateRole = (value) => {
    if (!value) return "User role is required";
    return "";
  };

  const validateOverTimeRate = (value, isAllowOverTime, ratePerHour) => {
    if (!isAllowOverTime) return "";
    let numValue = value ? parseFloat(value) : null;
    if (numValue === null) {
      numValue = ratePerHour ? parseFloat(ratePerHour) : null;
    }
    if (!numValue || numValue <= 0)
      return "Overtime rate must be a positive number";
    return "";
  };

  const validateSalary = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType === "Hourly") return "";
    if (!value || parseFloat(value) <= 0)
      return "Salary must be a positive number";
    return "";
  };

  const validateSalaryType = (value, isSalaryStaff) => {
    if (!isSalaryStaff) return "";
    if (!value || value.trim() === "")
      return "Salary type is required";
    return "";
  };

  const validateWeeklyHours = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType !== "Weekly") return "";
    const num = value != null && value !== "" ? parseFloat(value) : null;
    if (num === null || num <= 0) return "Weekly hours must be a positive number";
    return "";
  };

  const validateMonthlyHours = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType !== "Monthly") return "";
    const num = value != null && value !== "" ? parseFloat(value) : null;
    if (num === null || num <= 0) return "Monthly hours must be a positive number";
    return "";
  };

  const validateRatePerHour = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType !== "Hourly") return "";
    const num = value != null && value !== "" ? parseFloat(value) : null;
    if (num === null || num <= 0) return "Hourly rate must be a positive number";
    return "";
  };

  const handleInputChange = (
    field,
    value,
    validator,
    extraValidator = null
  ) => {
    setStaffData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({ ...prev, [field]: "" }));

    let error = validator ? validator(value) : "";
    if (extraValidator) {
      const extraError = extraValidator(value);
      if (extraError) error = extraError;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleSelectChange = (field, value, validator) => {
    let error = validator ? validator(value) : "";
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // Handle country/city changes from CountryCitySelector
  const handleCountryCityChange = (updatedData) => {
    setStaffData(prev => ({
      ...prev,
      // Keep country fixed from profile, do not update from selector
      country: prev.country,
      city: updatedData.city || "",
    }));
    
    // Validate city when it changes
    if (updatedData.city !== undefined) {
      const error = validateCity(updatedData.city);
      setErrors(prev => ({ ...prev, city: error }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation removed - will be auto-set from email
    newErrors.firstName = validateFirstName(staffData.firstName);
    newErrors.email = validateEmail(staffData.email);
    newErrors.password = validatePassword(staffData.password);
    newErrors.country = validateCountry(staffData.country);
    newErrors.city = validateCity(staffData.city);
    newErrors.passCode = validatePassCode(staffData.passCode);
    newErrors.userRoles = validateRole(selectedRole?.value);

    // Validate overtime for both salary and hourly staff
    newErrors.overTimeHourlyRate = validateOverTimeRate(
      staffData.overTimeHourlyRate,
      staffData.isAllowOverTime,
      staffData.ratePerHour
    );

    // Validate based on staff type
    if (staffData.isSalaryStaff) {
      newErrors.salary = validateSalary(staffData.salary, staffData.isSalaryStaff, staffData.salaryType);
      newErrors.salaryType = validateSalaryType(staffData.salaryType, staffData.isSalaryStaff);
      newErrors.weeklyHours = validateWeeklyHours(staffData.weeklyHours, staffData.isSalaryStaff, staffData.salaryType);
      newErrors.monthlyHours = validateMonthlyHours(staffData.monthlyHours, staffData.isSalaryStaff, staffData.salaryType);
      newErrors.ratePerHour = validateRatePerHour(staffData.ratePerHour, staffData.isSalaryStaff, staffData.salaryType);
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      showErrorToast("Please fix the errors below before submitting");
      return false;
    }

    return true;
  };

  const handleImageChange = (url, file) => {
    setImageState({
      staffImage: url,
      imageFile: file,
    });
  };

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      staffData,
      selectedRole,
      selectedManager,
      imageFileName: imageState?.imageFile?.name || imageState?.staffImage || "",
    });
  }, [staffData, selectedRole, selectedManager, imageState]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const HandleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    if (!validateForm()) {
      return false;
    }

    let {
      userName,
      firstName,
      lastName,
      email,
      country,
      city,
      phoneNo,
      passCode,
      ratePerHour,
      active,
      password,
      isAllowOverTime,
      isFullTimeStaff,
      isNewHireStaff,
      overTimeHourlyRate,
      isSalaryStaff,
      salaryType,
      salary,
      weeklyHours,
      monthlyHours,
    } = staffData;

    // Auto-set username from email
    userName = email;

    if (
      isAllowOverTime &&
      (!overTimeHourlyRate || overTimeHourlyRate === null)
    ) {
      overTimeHourlyRate = ratePerHour ? parseFloat(ratePerHour) : 0;
    }

    try {
      setLoading(true);
      let finalStaffImage = null;
      const baseURL = axiosInstance.defaults.baseURL || "";

      if (imageState.imageFile) {
        try {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);
          imageFormData.append("UserName", userName);
          imageFormData.append("CompID", CompID);

          const imageResponse = await axiosInstance.post(
            APIs.UPDATE_STAFF_IMAGE,
            imageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              params: {
                UserName: userName,
                CompID: CompID,
              },
            }
          );

          if (imageResponse.data?.status && imageResponse.data?.data?.filePath) {
            const relativePath = imageResponse.data.data.filePath;
            finalStaffImage = relativePath; // Store relative path for API
            
            // Construct full URL by prepending baseURL (matching backend logic)
            // Backend service does: _baseUrl + staffImagePath
            // Handle both cases: baseURL with/without trailing slash, path with/without leading slash
            let fullImageUrl;
            if (relativePath.startsWith("http")) {
              fullImageUrl = relativePath;
            } else {
              const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
              const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
              fullImageUrl = `${baseUrlClean}${pathClean}`;
            }
            
            // Update image state with full URL for display
            const displayUrl = `${fullImageUrl}?t=${Date.now()}`;
            setImageState({ staffImage: displayUrl, imageFile: null });
          } else {
            throw new Error(imageResponse.data?.message || "Image upload failed");
          }
        } catch (imageError) {
          // If image upload fails, preserve the original image state
          console.error("Image upload error:", imageError);
          showErrorToast(imageError.response?.data?.message || "Failed to upload image. Please try again.");
          setLoading(false);
          return; // Stop here if image upload fails
        }
      } else if (!imageState.staffImage || imageState.staffImage === "") {
        finalStaffImage = null;
      } else {
        // Extract relative path from full URL if it's already a full URL
        const currentImage = imageState.staffImage.split("?")[0];
        if (currentImage.startsWith("http")) {
          // Extract relative path from full URL
          const urlObj = new URL(currentImage);
          finalStaffImage = urlObj.pathname.startsWith("/") ? urlObj.pathname.substring(1) : urlObj.pathname;
        } else {
          finalStaffImage = currentImage;
        }
      }

      const response = await axiosInstance.post(APIs.ADD_STAFF, {
        UserName: userName,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Country: country,
        City: city,
        PhoneNo: phoneNo,
        PassCode: passCode,
        RatePerHour: isSalaryStaff && salaryType === "Hourly" && ratePerHour !== null ? parseFloat(ratePerHour) : null,
        Active: active,
        Password: password,
        CompID: CompID,
        ComRolePermission_ID: selectedRole.value,
        ManagerUserName: selectedManager?.value || "",
        IsAllowOverTime: isAllowOverTime,
        ISFullTimeStaff: isFullTimeStaff,
        ISNewHireStaff: isNewHireStaff,
        OverTimeHourlyRate: overTimeHourlyRate
          ? parseFloat(overTimeHourlyRate)
          : 0,
        IsSalaryStaff: isSalaryStaff,
        SalaryType: isSalaryStaff ? salaryType : "",
        Salary: isSalaryStaff && salary !== null ? parseFloat(salary) : null,
        WeeklyHours: isSalaryStaff && salaryType === "Weekly" && weeklyHours != null ? parseFloat(weeklyHours) : null,
        MonthlyHours: isSalaryStaff && salaryType === "Monthly" && monthlyHours != null ? parseFloat(monthlyHours) : null,
        StaffImage: finalStaffImage || null,
      });

      if (response.data.status) {
        showSuccessToast("Staff added successfully. Set their schedule on the next screen.");
        initialFormSnapshotRef.current = getFormSnapshot();
        // Save and Exit → manageStaff; Save (stay) → roster (continue create flow)
        if (exitAfterSave) {
          navigate("/staff/manageStaff");
        } else {
          navigate(`/staff/manageScheduleRoster/${encodeUrlParam(userName)}`);
        }
        return true;
      } else {
        showErrorToast(
          formatStaffIdentityError(response.data.message || response.data.Message) ||
            "Failed to add staff"
        );
        return false;
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.join(", ")
          : "An error occurred while adding the staff");
      showErrorToast(formatStaffIdentityError(errorMessage));
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => HandleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const cardStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
    color: isDarkMode ? "#ffffff" : "#000000",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
  };

  const sectionHeaderStyle = {
    color: isDarkMode ? "#ffffff" : "#495057",
    fontSize: "1.1rem",
    fontWeight: "600",
    marginBottom: "1.5rem",
    paddingBottom: "0.5rem",
    borderBottom: `2px solid ${themeColor}`,
  };

  // Error message style
  const errorStyle = {
    color: "#dc3545",
    fontSize: "0.875rem",
    marginTop: "0.25rem",
    fontWeight: "400",
  };

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/staff/manageStaff">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Add Staff</li>
          </ol>
          <h1 className="page-header mb-0">Add New Staff</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/staff/manageStaff")}
          onSave={() => HandleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => HandleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Staff Information</PanelHeader>
          <PanelBody>
            <div className="row g-4">
              {/* Top Row: Image Section and Status */}
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="text-center p-4" style={cardStyle}>
                    <ImageUpload
                      imageUrl={imageState.staffImage}
                      imageFile={imageState.imageFile}
                      setImage={handleImageChange}
                      className=""
                      centerButton={true}
                      useGrid={false}
                    />
                    <p
                      className="text-muted mt-3 mb-0"
                      style={{
                        fontSize: "0.9rem",
                        color: isDarkMode ? "#ffffff" : "#495057",
                      }}
                    >
                      <strong>Full Name:</strong> {staffData.firstName}{" "}
                      {staffData.lastName}
                    </p>
                    <p
                      className="text-muted"
                      style={{
                        fontSize: "0.9rem",
                        color: isDarkMode ? "#ffffff" : "#495057",
                      }}
                    >
                      <strong>Email:</strong> {staffData.email}
                    </p>
                    <p
                      className="text-muted"
                      style={{
                        fontSize: "0.9rem",
                        color: isDarkMode ? "#ffffff" : "#495057",
                      }}
                    >
                    </p>
                   
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-4 mb-3 mt-3" style={cardStyle}>
                    <h6 style={sectionHeaderStyle}>Status</h6>
                    <div className="row g-3">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={staffData.active}
                          onToggle={(checked) =>
                            setStaffData({ ...staffData, active: checked })
                          }
                          label="Active"
                          name="active"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>
                    <div className="row g-3 mt-3">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={staffData.isFullTimeStaff}
                          onToggle={(checked) =>
                            setStaffData({
                              ...staffData,
                              isFullTimeStaff: checked,
                            })
                          }
                          label="Full Time Staff"
                          name="fullTime"
                          themeColor={themeColor}
                        />
                      </div>
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={staffData.isNewHireStaff}
                          onToggle={(checked) =>
                            setStaffData({
                              ...staffData,
                              isNewHireStaff: checked,
                            })
                          }
                          label="New Hire Staff"
                          name="newHire"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Information Card */}
              <div className="col-md-6">
                <div className="p-4" style={cardStyle}>
                  <h6 style={sectionHeaderStyle}>Basic Information</h6>
                  <div className="row g-3">
                    {/* Username field hidden - will be auto-set from email */}
                    <div className="col-12">
                      <FormInput
                        label="First Name *"
                        placeHolder="Enter First Name"
                        type="text"
                        value={staffData.firstName}
                        onchange={(e) =>
                          handleInputChange(
                            "firstName",
                            e.target.value,
                            validateFirstName
                          )
                        }
                      />
                      {errors.firstName && (
                        <div style={errorStyle}>{errors.firstName}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Last Name"
                        placeHolder="Enter Last Name"
                        type="text"
                        value={staffData.lastName}
                        onchange={(e) =>
                          setStaffData({
                            ...staffData,
                            lastName: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Email *"
                        placeHolder="Enter Email"
                        type="email"
                        value={staffData.email}
                        onchange={(e) =>
                          handleInputChange(
                            "email",
                            e.target.value,
                            validateEmail
                          )
                        }
                      />
                      {errors.email && (
                        <div style={errorStyle}>{errors.email}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <label
                        className={isDarkMode ? "text-white" : "text-gray-700"}
                        style={{ fontWeight: "500" }}
                      >
                        Password *
                        <PasswordRequirementsInfo />
                      </label>
                      <div className="input-group">
                        <input
                          className="form-control"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter Password"
                          value={staffData.password}
                          onChange={(e) =>
                            handleInputChange(
                              "password",
                              e.target.value,
                              validatePassword
                            )
                          }
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          title={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          <i
                            className={`fa ${
                              showPassword ? "fa-eye-slash" : "fa-eye"
                            }`}
                          ></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleCopyPassword}
                          disabled={!staffData.password}
                          aria-label="Copy password"
                          title="Copy password"
                        >
                          <i className="fa fa-copy" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary dropdown-toggle"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          aria-label="Generate password"
                          title="Generate password"
                          style={{
                            color: themeColor,
                            borderColor: themeColor,
                          }}
                        >
                          <i className="fa fa-key me-1" aria-hidden="true" />
                          Generate
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          <li>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => handleGeneratePassword(8)}
                            >
                              Short (8 characters)
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => handleGeneratePassword(12)}
                            >
                              Strong (12 characters)
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => handleGeneratePassword(16)}
                            >
                              Extra strong (16 characters)
                            </button>
                          </li>
                        </ul>
                      </div>
                      {errors.password && (
                        <div style={errorStyle}>{errors.password}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Phone Number"
                        placeHolder="Enter Phone Number"
                        type="text"
                        value={staffData.phoneNo}
                        onchange={(e) =>
                          setStaffData({
                            ...staffData,
                            phoneNo: e.target.value,
                          })
                        }
                        prepend={companyDialCode || undefined}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Location & Role Card */}
              <div className="col-md-6">
                <div className="p-4 mb-3" style={cardStyle}>
                  <h6 style={sectionHeaderStyle}>Location & Role</h6>
                  <div className="row g-3">
                    {/* City Selector - Only show city based on profile country */}
                    <div className="col-12">
                      <CountryCitySelector
                        selectedCountry={staffData.country}
                        selectedCity={staffData.city}
                        setSelectedCity={(city) => {
                          setStaffData(prev => ({ ...prev, city: city || "" }));
                          const error = validateCity(city || "");
                          setErrors(prev => ({ ...prev, city: error }));
                        }}
                        onChange={handleCountryCityChange}
                        setLoading={null}
                        showCountryField={false}
                        showCityField={true}
                        cityLabel="City *"
                        cityPlaceholder="Select City"
                        isDarkMode={isDarkMode}
                        selectedValueIsLabel={true}
                      />
                      {errors.city && <div style={errorStyle}>{errors.city}</div>}
                    </div>
                    <div className="col-12">
                      <label
                        className={isDarkMode ? "text-white" : "text-gray-700"}
                        style={{ fontWeight: "500" }}
                      >
                        User Roles *
                      </label>
                      <Select
                        value={selectedRole}
                        onChange={(option) => {
                          const newValue = option ? option.value : null;
                          setSelectedRole(option);
                          setStaffData({ ...staffData, userRoles: newValue });
                          handleSelectChange(
                            "userRoles",
                            newValue,
                            validateRole
                          );
                        }}
                        options={roles}
                        styles={dropdownStyles}
                        placeholder="Select Role"
                        isClearable={true}
                      />
                      {errors.userRoles && (
                        <div style={errorStyle}>{errors.userRoles}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <label
                        className={isDarkMode ? "text-white" : "text-gray-700"}
                        style={{ fontWeight: "500" }}
                      >
                        Manager
                      </label>
                      <Select
                        value={selectedManager}
                        onChange={(option) => {
                          const newValue = option ? option.value : "";
                          setSelectedManager(option);
                          setStaffData({
                            ...staffData,
                            managerUserName: newValue,
                          });
                        }}
                        options={managers}
                        styles={dropdownStyles}
                        placeholder="Select Manager"
                        isClearable={true}
                      />
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Pass Code *"
                        placeHolder="Enter Pass Code"
                        type="text"
                        value={staffData.passCode}
                        onchange={(e) =>
                          handleInputChange(
                            "passCode",
                            e.target.value,
                            validatePassCode
                          )
                        }
                      />
                      {errors.passCode && (
                        <div style={errorStyle}>{errors.passCode}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4" style={cardStyle}>
                  <h6 style={sectionHeaderStyle}>Compensation</h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <ToggleSwitch
                        isChecked={staffData.isSalaryStaff}
                        onToggle={(checked) =>
                          setStaffData({
                            ...staffData,
                            isSalaryStaff: checked,
                            // Clear opposite fields when toggling (keep overtime in both cases)
                            ratePerHour: checked ? null : staffData.ratePerHour,
                            salary: checked ? staffData.salary : null,
                            salaryType: checked ? staffData.salaryType : "Weekly",
                          })
                        }
                        label="Salary Staff"
                        name="salaryStaff"
                        themeColor={themeColor}
                      />
                    </div>

                    {/* Salary Fields - Show when isSalaryStaff is true */}
                    {staffData.isSalaryStaff && (
                      <>
                        <div className="col-12">
                          <label
                            className={isDarkMode ? "text-white" : "text-gray-700"}
                            style={{ fontWeight: "500" }}
                          >
                            Salary Type *
                          </label>
                          <Select
                            value={
                              staffData.salaryType
                                ? { value: staffData.salaryType, label: staffData.salaryType }
                                : null
                            }
                            onChange={(option) => {
                              const newValue = option ? option.value : "Weekly";
                              setStaffData({ ...staffData, salaryType: newValue });
                              handleSelectChange(
                                "salaryType",
                                newValue,
                                (val) => validateSalaryType(val, staffData.isSalaryStaff)
                              );
                              setErrors((prev) => ({
                                ...prev,
                                ratePerHour: validateRatePerHour(
                                  staffData.ratePerHour,
                                  staffData.isSalaryStaff,
                                  newValue
                                ),
                              }));
                            }}
                            options={[
                              { value: "Weekly", label: "Weekly" },
                              { value: "Monthly", label: "Monthly" },
                              { value: "Hourly", label: "Hourly" },
                            ]}
                            styles={dropdownStyles}
                            placeholder="Select Salary Type"
                            isClearable={false}
                          />
                          {errors.salaryType && (
                            <div style={errorStyle}>{errors.salaryType}</div>
                          )}
                        </div>
                        {staffData.salaryType !== "Hourly" && (
                          <div className="col-12">
                            <FormInput
                              label={`${staffData.salaryType} Salary *`}
                              placeHolder={`Enter ${staffData.salaryType} Salary`}
                              type="number"
                              step="0.01"
                              value={staffData.salary || ""}
                              onchange={(e) =>
                                handleInputChange(
                                  "salary",
                                  e.target.value ? parseFloat(e.target.value) : null,
                                  null,
                                  () => validateSalary(
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    staffData.isSalaryStaff,
                                    staffData.salaryType
                                  )
                                )
                              }
                            />
                            {errors.salary && (
                              <div style={errorStyle}>{errors.salary}</div>
                            )}
                          </div>
                        )}
                        {staffData.salaryType === "Weekly" && (
                          <div className="col-12">
                            <FormInput
                              label="Weekly Hours *"
                              placeHolder="Enter Weekly Hours"
                              type="number"
                              step="0.01"
                              value={staffData.weeklyHours ?? ""}
                              onchange={(e) =>
                                handleInputChange(
                                  "weeklyHours",
                                  e.target.value ? parseFloat(e.target.value) : null,
                                  null,
                                  () => validateWeeklyHours(
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    staffData.isSalaryStaff,
                                    staffData.salaryType
                                  )
                                )
                              }
                            />
                            {errors.weeklyHours && (
                              <div style={errorStyle}>{errors.weeklyHours}</div>
                            )}
                          </div>
                        )}
                        {staffData.salaryType === "Monthly" && (
                          <div className="col-12">
                            <FormInput
                              label="Monthly Hours *"
                              placeHolder="Enter Monthly Hours"
                              type="number"
                              step="0.01"
                              value={staffData.monthlyHours ?? ""}
                              onchange={(e) =>
                                handleInputChange(
                                  "monthlyHours",
                                  e.target.value ? parseFloat(e.target.value) : null,
                                  null,
                                  () => validateMonthlyHours(
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    staffData.isSalaryStaff,
                                    staffData.salaryType
                                  )
                                )
                              }
                            />
                            {errors.monthlyHours && (
                              <div style={errorStyle}>{errors.monthlyHours}</div>
                            )}
                          </div>
                        )}
                        {staffData.salaryType === "Hourly" && (
                          <div className="col-12">
                            <FormInput
                              label="Hourly Rate *"
                              placeHolder="Enter Hourly Rate"
                              type="number"
                              step="0.01"
                              value={staffData.ratePerHour ?? ""}
                              onchange={(e) =>
                                handleInputChange(
                                  "ratePerHour",
                                  e.target.value ? parseFloat(e.target.value) : null,
                                  null,
                                  () =>
                                    validateRatePerHour(
                                      e.target.value ? parseFloat(e.target.value) : null,
                                      staffData.isSalaryStaff,
                                      staffData.salaryType
                                    )
                                )
                              }
                            />
                            {errors.ratePerHour && (
                              <div style={errorStyle}>{errors.ratePerHour}</div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Overtime - Always visible for both salary and hourly staff */}
                    <div className="col-12">
                      <ToggleSwitch
                        isChecked={staffData.isAllowOverTime}
                        onToggle={(checked) =>
                          setStaffData({
                            ...staffData,
                            isAllowOverTime: checked,
                            overTimeHourlyRate: checked
                              ? staffData.overTimeHourlyRate || null
                              : null,
                          })
                        }
                        label="Allow Overtime"
                        name="allowOvertime"
                        themeColor={themeColor}
                      />
                      {staffData.isAllowOverTime && (
                        <div className="mt-3">
                          <FormInput
                            label="Overtime Hourly Rate"
                            placeHolder="Enter Overtime Rate"
                            type="number"
                            step="0.01"
                            value={staffData.overTimeHourlyRate || ""}
                            onchange={(e) =>
                              handleInputChange(
                                "overTimeHourlyRate",
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : null,
                                null,
                                () =>
                                  validateOverTimeRate(
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : null,
                                    staffData.isAllowOverTime,
                                    staffData.ratePerHour
                                  )
                              )
                            }
                          />
                          {errors.overTimeHourlyRate && (
                            <div style={errorStyle}>
                              {errors.overTimeHourlyRate}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Staff Schedule info hidden — schedule is set on Schedule tab after save (redirect still applies). */}
              {/*
              <div className="col-12">
                <div className="p-4" style={cardStyle}>
                  <h6 style={sectionHeaderStyle}>Staff Schedule</h6>
                  <p className="mb-0 text-muted">
                    Working hours are managed in <strong>Schedule</strong> after this staff member is
                    saved. You will be taken to the schedule screen to set their first week.
                  </p>
                </div>
              </div>
              */}
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default AddStaff;